import { useEffect, useState, type FormEvent } from "react";
import { countryOption, type CountrySelection } from "@moneymap/domain";
import { useAuth } from "../auth/AuthProvider";
import { CompactAssessmentLinks } from "./AssessmentValue";

interface PublicAnalyticsWindow extends Window {
  clarity?: (method: string, ...args: unknown[]) => void;
}

function trackPublicEvent(name: string): void {
  (window as PublicAnalyticsWindow).clarity?.("event", name);
}

interface AuthFailureCopy {
  event: string;
  message: string;
}

function authErrorDetails(error: unknown): { message: string; status: number | null } {
  const record = typeof error === "object" && error !== null ? error as Record<string, unknown> : null;
  const rawMessage = error instanceof Error
    ? error.message
    : typeof record?.message === "string"
      ? record.message
      : "";
  const code = typeof record?.code === "string" ? record.code : "";
  const status = typeof record?.status === "number" ? record.status : null;
  return { message: `${rawMessage} ${code}`.toLowerCase(), status };
}

function authFailureCopy(error: unknown): AuthFailureCopy {
  const { message, status } = authErrorDetails(error);

  if (status === 429 || message.includes("rate") || message.includes("too many") || message.includes("too soon") || message.includes("60 seconds")) {
    return {
      event: "sign_in_code_request_failed_rate_limit",
      message: "That was too soon. Wait one minute, then request another code."
    };
  }
  if (message.includes("invalid email") || message.includes("invalid_email")) {
    return {
      event: "sign_in_code_request_failed_invalid_email",
      message: "That email address does not look valid. Check it and try again."
    };
  }
  if (message.includes("failed to fetch") || message.includes("network") || message.includes("connection")) {
    return {
      event: "sign_in_code_request_failed_network",
      message: "We couldn’t reach the sign-in service. Check your connection and try again."
    };
  }
  if (message.includes("not configured") || message.includes("not enabled") || message.includes("unavailable")) {
    return {
      event: "sign_in_code_request_failed_unavailable",
      message: "Sign-in is temporarily unavailable. Please try again in a few minutes."
    };
  }
  if (message.includes("delivery") || message.includes("send")) {
    return {
      event: "sign_in_code_request_failed_delivery",
      message: "We couldn’t send the email. Check the address, wait a moment and try again."
    };
  }
  return {
    event: "sign_in_code_request_failed_unknown",
    message: "We couldn’t send a code. Check the address and try again."
  };
}

function verificationFailureCopy(error: unknown): AuthFailureCopy {
  const { message, status } = authErrorDetails(error);

  if (status === 429 || message.includes("too many") || message.includes("disabled")) {
    return {
      event: "sign_in_code_verification_failed_rate_limit",
      message: "There were too many attempts. Request a new code and try again in a minute."
    };
  }
  if (message.includes("expired") || message.includes("invalid") || message.includes("token")) {
    return {
      event: "sign_in_code_verification_failed_invalid",
      message: "That code is incorrect or has expired. Check the email, or request a new code."
    };
  }
  if (message.includes("failed to fetch") || message.includes("network") || message.includes("connection")) {
    return {
      event: "sign_in_code_verification_failed_network",
      message: "We couldn’t check the code. Check your connection and try again."
    };
  }
  return {
    event: "sign_in_code_verification_failed_unknown",
    message: "We couldn’t verify that code. Check it and try again."
  };
}

export function AuthPanel({ onClose, planningSelection, onChangeCountry, initialEmail = "", checkoutReturned = false }: { onClose?: () => void; planningSelection?: CountrySelection | undefined; onChangeCountry?: (() => void) | undefined; initialEmail?: string | undefined; checkoutReturned?: boolean | undefined }) {
  const auth = useAuth();
  const planningCountry = planningSelection ? countryOption(planningSelection.country) : null;
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  useEffect(() => {
    trackPublicEvent(onClose ? "sign_in_dialog_opened" : "sign_in_page_opened");
  }, [onClose]);

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timeout = window.setTimeout(() => setResendSeconds((seconds) => Math.max(0, seconds - 1)), 1_000);
    return () => window.clearTimeout(timeout);
  }, [resendSeconds]);

  async function sendCode(isResend = false) {
    if (busy || (isResend && resendSeconds > 0)) return;
    setBusy(true);
    setStatus(null);
    try {
      await auth.requestEmailCode(email);
      trackPublicEvent("sign_in_code_requested");
      setCodeSent(true);
      setResendSeconds(60);
      setStatus("We sent a six-digit code. It may take a minute to arrive.");
    } catch (error) {
      trackPublicEvent("sign_in_code_request_failed");
      const failure = authFailureCopy(error);
      trackPublicEvent(failure.event);
      setStatus(failure.message);
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      await auth.verifyEmailCode(email, code);
      trackPublicEvent("sign_in_code_verified");
      setStatus("You’re in. Loading your saved answers…");
    } catch (error) {
      trackPublicEvent("sign_in_code_verification_failed");
      const failure = verificationFailureCopy(error);
      trackPublicEvent(failure.event);
      setStatus(failure.message);
    } finally {
      setBusy(false);
    }
  }

  const panel = (
      <section className="auth-panel" role={onClose ? "dialog" : "region"} aria-modal={onClose ? "true" : undefined} aria-labelledby="auth-title">
        {onClose && <button className="modal-close" type="button" aria-label="Close sign-in" onClick={onClose}>×</button>}
        {planningCountry && <div className="auth-country-context"><div><span>Planning country</span><strong>{planningCountry.label} · {planningSelection?.currency}</strong></div>{onChangeCountry && <button type="button" onClick={onChangeCountry}>Change</button>}</div>}
        <p className="eyebrow">{checkoutReturned ? "Checkout returned · link your purchase" : "Start free · no card"}</p>
        <h2 id="auth-title">{checkoutReturned ? "Sign in to attach lifetime Pro to your account." : "Where should we send your sign-in code?"}</h2>
        <p>{checkoutReturned ? "Use the same email you entered at checkout. We’ll verify it with a six-digit code, then confirm your lifetime access." : "There’s no password. The check takes about three minutes, saves as you go, and keeps you signed in on this device until you sign out."}</p>
        {!auth.configured ? (
          <div className="notice warning"><strong>Sign-in is temporarily unavailable</strong><span>The AppCradle Auth project settings need attention before onboarding can begin.</span></div>
        ) : (
          <>
            {!codeSent ? (
              <form onSubmit={(event) => { event.preventDefault(); void sendCode(false); }}>
                <label className="field" htmlFor="auth-email">Email address<input id="auth-email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" /></label>
                <button className="primary-button full-width" disabled={busy} type="submit">{busy ? "Sending…" : "Send me the code"}</button>
              </form>
            ) : (
              <form onSubmit={(event) => { void verifyCode(event); }}>
                <label className="field" htmlFor="auth-code">Six-digit code<input id="auth-code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" /></label>
                <button className="primary-button full-width" disabled={busy || code.length !== 6} type="submit">{busy ? "Checking…" : "Verify and sign in"}</button>
                <button className="text-button full-width" disabled={busy || resendSeconds > 0} type="button" onClick={() => { void sendCode(true); }}>{resendSeconds > 0 ? `Resend in ${resendSeconds}s` : "Resend code"}</button>
                <button className="text-button full-width" disabled={busy} type="button" onClick={() => { setCodeSent(false); setCode(""); setStatus(null); setResendSeconds(0); }}>Use a different email</button>
              </form>
            )}
          </>
        )}
        {status && <p className="auth-status" role="status">{status}</p>}
        <p className="fine-print">Your first risk brief is free. The full plan is optional and costs $25 once. Your answers are encrypted, never sold, and reminders stay off unless you turn them on. <a href="mailto:appcradledevs@gmail.com?subject=MoneyMap%20sign-in%20help">Need sign-in help?</a></p>
        <p className="legal-links"><a href="/privacy/">Privacy</a><span aria-hidden="true"> · </span><a href="/terms/">Terms</a></p>
      </section>
  );

  if (onClose) {
    return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>{panel}</div>;
  }

  return (
    <main className="auth-gate" id="main-content">
      <section className="auth-gate-copy" aria-labelledby="welcome-heading">
        <p className="eyebrow">{planningCountry ? `${planningCountry.label} · ${planningSelection?.currency}` : "Worldwide household check"}</p>
        <h1 id="welcome-heading">Would your household be okay if an income stopped or a large cost arrived?</h1>
        <p>Give us a few rough numbers. We’ll check savings, debts and reported protection together, using your selected currency and the appropriate country-rule depth.</p>
        <div className="auth-output-preview" aria-label="Example MoneyMap result">
          <div className="preview-top"><span>Here’s the sort of answer you’ll get</span><strong>EXAMPLE</strong></div>
          <div className="preview-route"><div><small>Required monthly outgo</small><b>1 month</b></div><i>÷</i><div><small>Accessible money now</small><b>0.6 month</b></div><i>=</i><div><small>Time covered</small><b>18 days</b></div></div>
          <div className="preview-result"><span>This household would run out of accessible savings in about 18 days.</span><strong>First job: close the remaining 0.4-month gap.</strong><small>After that, check what the household would receive if its only earner could no longer work.</small></div>
        </div>
        <div className="auth-included-checks">
          <div><strong>This is not only an emergency-fund calculator.</strong><span>The same saved answers power all eight checks.</span></div>
          <CompactAssessmentLinks />
        </div>
        <a className="sample-report-link" href={`/sample-household/${planningSelection ? `?country=${planningSelection.country}&currency=${planningSelection.currency}` : ""}`}>See a complete example before you sign in →</a>
        <ul>
          <li><strong>Estimates are fine</strong><span>Nothing has to be exact today</span></li>
          <li><strong>Stop whenever you need to</strong><span>Your last step will be here when you return</span></li>
          <li><strong>No insurance or investment sales</strong><span>We don’t earn commission from your answer</span></li>
        </ul>
      </section>
      {panel}
    </main>
  );
}
